<template>
	<anchor-element tag="span" :chain="chain" :tabindex="tabindex" class="select-tree like-select" :class="{ 'read-only':disabled }" :style="fieldStyle"
					@focus="fieldFocus" @blur="fieldBlur" @mousedown="fieldMouseDown" @keydown="keyDown">
		<span v-if="showPlaceholder" class="st-placeholder">{{ placeholder }}</span>
		<span v-else class="st-selected">{{ text }}</span>
	</anchor-element>
	<boat-element :tag="SelectTreePanel" :vertical-correction="true" :items="tree.items" v-model="dataValue" v-model:selection="selectedValue" :chain="chain" :dir="dir"></boat-element>
	<input v-if="property" type="hidden" :name="property" v-model.number="propertyValue" :id="propertyId == null ? property : propertyId">
</template>

<script setup lang="ts">
import {ref, computed, onMounted, onUnmounted, watch, nextTick, CSSProperties, provide} from "vue";
import type {SelectHierarchy, SelectHierarchyItem} from "@t/ui/SelectHierarchy.ts";
import {closeAll, createChain, DROPDOWN_PLACEMENT, DROPUP_PLACEMENT} from "@/ui/paneltree/PanelTree.ts";
import AnchorElement from "@/ui/paneltree/AnchorElement.vue";
import BoatElement from "@/ui/paneltree/BoatElement.vue";
import SelectTreePanel from "@/ui/select/SelectTreePanel.vue";
import {ElementMeasure} from "@fd/helpers/DomUtils.ts";
import {findItemIn, pathTo} from "@/ui/select/SelectTreeUtils.ts";


const props = withDefaults(defineProps<{
	tree: SelectHierarchy,
	modelValue: string|null|undefined,
	disabled?: boolean,
	tabindex?: number,
	placeholder?: string,
	dir: "left" | "right",
	required?: boolean,
	/* If set the component will synchronize model value with hidden input element with this name.
	 * modelValue is used only to initialize input/hidden element */
	property?: string;
	/** This ID will be given to input.hidden generated for the property. If missing ID will be the same as property */
	propertyId?: string;
	/** Set width of the field */
	width?: string;
}>(), {
	disabled: false,
	tabindex: 0,
	placeholder: "",
	dir: "right",
	required: false
});
const propertyValue = ref<string|null>(props.modelValue);
const emit = defineEmits(['update:modelValue', 'change']);
const hasHover = ref(false);
const pointerDownTimestamp = ref<number>(0);
provide("pointerDownTimestamp", pointerDownTimestamp);

const dataValue = computed<string>({
	get():string {
		return props.property ? propertyValue.value : props.modelValue;
	},
	set(v: string):void {
		const currentValue = props.property ? propertyValue.value : props.modelValue;
		if (props.property) propertyValue.value = v;
		else emit('update:modelValue', v);
		if (v !== currentValue) {
			/** The change event must be fired after DOM update (extenal code can access hidden property) */
			nextTick(() => emit('change', v));
		}
	}
});

const selectedValue = ref<string|null>(props.modelValue);

function detectHoverCapability() {
	hasHover.value = window.matchMedia('(hover: hover)').matches;
}

const chain = createChain({
	rule: (m:ElementMeasure) => {
		if (m.spaceBottom < 100 && m.spaceTop > m.spaceBottom) {
			return { ...DROPUP_PLACEMENT };
		}
		return { ...DROPDOWN_PLACEMENT };
	}
}, "select-tree");

const fieldStyle = computed<CSSProperties>(() => {
	const s:CSSProperties = {};
	if (props.width) s.width = props.width;
	return s;
});

let blurTimeout = 0;

function fieldFocus() {
	//console.log("SelectTree FOCUS");
	if (blurTimeout) {
		clearTimeout(blurTimeout);
		blurTimeout = 0;
	}
}

function stopEditing():void {
	chain.visible.value = false;
	selectedValue.value = dataValue.value;
}

function startEditing():void {
	chain.visible.value = true;
	selectedValue.value = null;
}

function fieldBlur() {
	//console.log("SelectTree BLUR");
	blurTimeout = setTimeout(stopEditing, 150);
}

function fieldMouseDown(e:MouseEvent) {
	//console.log("SelectTree mousedown");
	if (props.disabled) return;
	e.stopPropagation();	// This will prevent closing of other drop-downs and menus
	closeAll();				// and so we must close them manually

	pointerDownTimestamp.value = e.timeStamp;
	startEditing();	// make root panel visible
	if (blurTimeout) {
		clearTimeout(blurTimeout);
		blurTimeout = 0;
	}
}

function moveSelection(keyName: string):void {
	if (selectedValue.value != null) {
		const path = pathTo(selectedValue.value, props.tree);
		if (path.length > 0) {
			const last = path[path.length - 1];
			switch (keyName) {
				case "ArrowUp":
					if (last.index > 0) {
						selectedValue.value = last.list.items[last.index - 1].id;
					}
					break;
				case "ArrowDown":
					if (last.index < (last.list.items.length - 1)) {
						selectedValue.value = last.list.items[last.index + 1].id;
					}
					break;
			}
		}
	}
}

function applySelection():void {
	if (selectedValue.value != null) {
		const path = pathTo(selectedValue.value, props.tree);
		if (path.length > 0) {
			const last = path[path.length - 1];
			const item = last.list.items[last.index];
			if (Array.isArray(item?.submenu?.items) && item.submenu.items.length > 0) {
				selectedValue.value = item.submenu.items[0].id;
			} else {
				// leaf item
				dataValue.value = selectedValue.value;
			}
		}
	}
}

function escapeSelection():void {
	if (selectedValue.value != null) {
		const path = pathTo(selectedValue.value, props.tree);
		if (path.length > 1) {
			const previous = path[path.length - 2];
			const item = previous.list.items[previous.index];
			selectedValue.value = item.id;
			return;
		}
	}
	stopEditing();
}

function keyDown(e:KeyboardEvent) {
	if (props.disabled) return;
	//console.log("Key "+e.key);
	switch (e.key) {
		case "ArrowUp":
		case "ArrowDown":
			if (chain.visible.value) {
				moveSelection(e.key);
			} else chain.visible.value = true;
			e.preventDefault();
			break;
		case "Enter":
		case " ":
		case "ArrowRight":
			e.preventDefault();
			if (chain.visible.value) applySelection();
			else startEditing();
			break;
		case "Escape":
		case "ArrowLeft":
			e.preventDefault();
			if (chain.visible.value) escapeSelection();
			break;
	}
}

function findItem(id: string):SelectHierarchyItem|null {
	return findItemIn(id, props.tree.items);
}

const selected = computed<SelectHierarchyItem|null>(() => {
	return findItem(dataValue.value);
});

const showPlaceholder = computed<boolean>(() => dataValue.value == null || dataValue.value.length === 0 );
const text = computed<string>(() => {
	const s = selected.value;
	if (s == null) return "(" + dataValue.value + ")";
	return s.text;
});

let hoverMediaQuery:MediaQueryList|null = null;

onMounted(() => {
	hoverMediaQuery = window.matchMedia('(hover: hover)');
	detectHoverCapability();
	hoverMediaQuery.addEventListener('change', detectHoverCapability);
});

onUnmounted(() => {
	if (hoverMediaQuery) {
		hoverMediaQuery.removeEventListener('change', detectHoverCapability);
		hoverMediaQuery = null;
	}
});

watch(dataValue, () => {
	chain.close();
});
</script>

<style>

.select-tree {
	display: inline-flex;
	vertical-align: baseline;
	box-sizing: border-box;
	min-height: var(--input-height);
	min-width: 2ch;
	user-select: none;
	-webkit-user-select: none;
	cursor: default;

	& > .st-selected,
	& > .st-placeholder {
		display: inline-block;
		vertical-align: baseline;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		max-width: 100%;

		&:empty:before {
			content: 'x';
			color: #00000000;
		}
	}
	& > .st-placeholder {
		color: var(--placeholder-fg);
	}
}

</style>